import React from 'react';
import { serviceCards } from '../data/index';

interface ServiceCard {
    icon: string;
    title: string;
    subtitle: string;
    description: string;
    benefits: string[];
    ctaLabel: string;
    ctaHref: string;
}

/**
 * Boxicons 2.1.4 (loaded in index.html) does not ship glyphs for a handful of
 * names. To keep the data file untouched while still rendering a meaningful
 * icon, we alias each missing class to the closest existing boxicon.
 */
const BOXICON_ALIASES: Record<string, string> = {
    'bx bx-core': 'bx bx-target-lock',
    'bx bx-route': 'bx bx-trending-up',
    'bx bx-brain-circuit': 'bx bx-brain',
};

const resolveBoxicon = (icon: string): string => {
    const trimmed = icon.trim().replace(/\s+/g, ' ');
    return BOXICON_ALIASES[trimmed] ?? trimmed;
};

const Services: React.FC = () => {
    return (
        <section
            aria-labelledby="services-heading"
            className="w-full"
        >
            <div className="mb-10 sm:mb-14 text-center max-w-3xl mx-auto px-2">
                <span className="inline-block px-3 py-1 text-[10px] sm:text-xs font-bold tracking-widest text-emerald-400 uppercase bg-emerald-400/10 rounded-full border border-emerald-400/20">
                    Core Services
                </span>
                <h2
                    id="services-heading"
                    className="mt-4 text-xl sm:text-2xl lg:text-3xl font-black tracking-tight text-slate-900 dark:text-white leading-snug text-balance"
                >
                    Professional-grade financial tools and insights for investors at every stage of
                    their journey.{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-emerald-300">
                        Analyze opportunities, understand risks, and build wealth with greater
                        confidence.
                    </span>
                </h2>
            </div>

            <ul
                role="list"
                className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-7"
            >
                {(serviceCards as ServiceCard[]).map((card) => (
                    <li key={card.title} className="group flex">
                        <article
                            className="relative flex h-full w-full flex-col rounded-3xl p-6 sm:p-8
                                       bg-white/80 dark:bg-slate-900/70
                                       border border-slate-200 dark:border-slate-800
                                       backdrop-blur-sm
                                       shadow-sm hover:shadow-2xl hover:shadow-emerald-500/10
                                       transition-all duration-300
                                       hover:-translate-y-1 hover:border-emerald-400/60"
                        >
                            {/* Icon */}
                            <div className="flex items-center justify-center w-14 h-14 rounded-2xl
                                            bg-gradient-to-br from-green-500/20 to-emerald-300/10
                                            border border-emerald-400/20
                                            text-emerald-500 dark:text-emerald-400
                                            group-hover:from-green-500 group-hover:to-emerald-300
                                            group-hover:text-white
                                            transition-all duration-300">
                                <i className={`${resolveBoxicon(card.icon)} text-3xl`} aria-hidden="true"></i>
                            </div>

                            {/* Title */}
                            <h3 className="mt-5 text-xl sm:text-2xl font-black tracking-tight text-slate-900 dark:text-white leading-snug">
                                {card.title}
                            </h3>

                            {/* Subtitle */}
                            <p className="mt-2 text-sm sm:text-base font-semibold text-emerald-600 dark:text-emerald-400 leading-snug">
                                {card.subtitle}
                            </p>

                            {/* Description */}
                            <p className="mt-4 text-sm sm:text-[15px] text-slate-600 dark:text-slate-300 leading-relaxed">
                                {card.description}
                            </p>

                            {/* Key Benefits */}
                            <div className="mt-6">
                                <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-3">
                                    Key Benefits
                                </p>
                                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                                    {card.benefits.map((benefit) => (
                                        <li
                                            key={benefit}
                                            className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200"
                                        >
                                            <i className="bx bx-check text-lg text-emerald-500 dark:text-emerald-400 leading-none shrink-0" aria-hidden="true"></i>
                                            <span className="leading-snug">{benefit}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* CTA */}
                            <div className="mt-auto pt-7">
                                <a
                                    href={card.ctaHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group/cta inline-flex w-full items-center justify-center gap-2
                                               rounded-xl px-6 py-3.5
                                               bg-gradient-to-r from-green-500 to-emerald-400
                                               text-white text-sm sm:text-base font-bold
                                               shadow-lg shadow-emerald-500/25
                                               hover:shadow-xl hover:shadow-emerald-500/40
                                               hover:from-green-400 hover:to-emerald-300
                                               hover:-translate-y-0.5
                                               focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900
                                               transition-all duration-300"
                                >
                                    {card.ctaLabel}
                                </a>
                            </div>
                        </article>
                    </li>
                ))}
            </ul>
        </section>
    );
};

export default Services;
